<template>
  <v-container fluid>
    <v-card-title v-if="!pdfMode">
      {{ $t(`${labelsKey}.label.${type}`) }}
      <v-spacer />
      <v-btn
        small
        rounded
        outlined
        color="primary"
        @click="$router.push({ name: `${type}s` })"
      >
        {{ $t(`${labelsKey}.goto.${type}`) }}
        <v-icon small class="ml-1">mdi-open-in-new</v-icon>
      </v-btn>
    </v-card-title>
    <h2 v-else-if="showTitle" class="pdf-mode">{{ $t(`${labelsKey}.label.${type}`) }}</h2>
    <v-card-text :class="[{'pdf-mode': pdfMode}]">
      <v-data-table
        :headers="headers"
        :items="sortedItems"
        item-key="id"
        :items-per-page="-1"
        :single-expand="false"
        :show-expand="!pdfMode"
        :expanded.sync="expandedItems"
        :dense="pdfMode"
        :hide-default-footer="pdfMode"
      >
        <template v-slot:item.no="{ item }">
          <v-chip :class="[colorForStage[item.stage], {'pdf-mode': pdfMode}]">
            {{item.no}}
          </v-chip>
        </template>

        <template v-slot:item.estimatedValue="{ item }">
          <span v-if="item.estimatedValueCents.sum !== null">
            <PricesDisplay
              class="pt-2"
              :prices="item.estimatedValueCents"
              :contract-currencies="currencies"
              :chosen-currency="chosenCurrency"
              :item-currency="chosenCurrency"
            />
          </span>
        </template>

        <template v-slot:item.originalValue="{ item }">
          <span class="nowrap" v-if="item.originalValueCents.sum !== null">
            <PricesDisplay
              class="pt-2"
              :prices="item.originalValueCents"
              :contract-currencies="currencies"
              :chosen-currency="chosenCurrency"
              :item-currency="chosenCurrency"
            />
          </span>
        </template>

        <template v-slot:item.agreedValue="{ item }">
          <span class="nowrap" v-if="item.agreedValueCents.sum !== null">
            <PricesDisplay
              class="pt-2"
              :prices="item.agreedValueCents"
              :contract-currencies="currencies"
              :chosen-currency="chosenCurrency"
              :item-currency="chosenCurrency"
            />
          </span>
        </template>

        <template v-slot:item.receivedDate="{ item }">
          {{ item.receivedDate | formatDate }}
        </template>

        <template v-slot:item.eotRequested="{ item }">
          <span v-if="item.eotRequested !== null" class="eot-cell">
            <v-icon
              class="primary--text"
              small
            >mdi-calendar-clock</v-icon>
            {{item.eotRequested.sum }}
          </span>
        </template>

        <template v-slot:item.eotAgreed="{ item }">
          <span v-if="item.eotAgreed !== null" class="eot-cell">
            <v-icon
              class="success--text"
              small
            >mdi-timetable</v-icon> {{item.eotAgreed.sum }}
          </span>
        </template>

        <template v-slot:item.description="{ item }">
          <div v-if="item.description" class="notes-cell">
            <span class="text--secondary">{{ pdfMode ? item.description.replace(/\n/g, ' ') : item.description }}</span>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <div class="">
            <v-chip
              :class="[statusesMap[item.status] ? statusesMap[item.status].color : '', {'pdf-mode': pdfMode}]"
              small
            >{{statusesMap[item.status] ? statusesMap[item.status].name : ''}}</v-chip>
          </div>
        </template>
        <template v-slot:expanded-item="{ item }">
          <td :colspan="headers.length" class="pa-3">
            <VariationsPartsTable
              :parts="item.parts"
              :reporter="item.reportingSide"
              :isMultiCurrency="isMultiCurrency"
            />
          </td>
        </template>
      </v-data-table>
    </v-card-text>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'VariationReportTable',
  props: {
    items: Array,
    type: String,
    chosenCurrency: Object,
    currencies: Array,
    showParts: Boolean,
    parts: Array,
    isMultiCurrency: Boolean,
    pdfMode: Boolean,
    labelsKey: {
      type: String,
      default: 'reports.openVariations'
    },
    showTitle: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      colorForStage: ['', 'orange', 'primary'],
      headers: [
        { text: this.$t('variation.headers.no'), value: 'no' },
        { text: this.$t('variation.headers.title'), value: 'name' },
        this.type !== 'amendment' ? { text: this.$t('variation.headers.estimatedValue'), value: 'estimatedValue' } : null,
        this.type !== 'amendment' ? { text: this.$t('variation.headers.originalValue'), value: 'originalValue' } : null,
        { text: this.$t('variation.headers.agreedValue'), value: 'agreedValue' },
        this.type !== 'amendment' ? { text: this.$t('variation.headers.eotRequested'), value: 'eotRequested' } : null,
        this.type !== 'amendment' ? { text: this.$t('variation.headers.eotAgreed'), value: 'eotAgreed' } : null,
        { text: this.$t('variation.notes'), value: 'description' },
        { text: this.$t('variation.headers.anticipatedClosingDate'), value: 'receivedDate' },
        { text: this.$t('variation.headers.status'), value: 'status' },
        { text: '', value: 'data-table-expand' }
      ].filter(i => !!i),
      expandedItems: []
    }
  },
  computed: {
    ...mapState({
      statusesMap: (state) => state.statuses.reduce((acc, curr) => ({ ...acc, [curr.code]: curr }), {})
    }),
    sortedItems () {
      const items = cloneDeep(this.items)
      items
        .sort((a, b) => b.agreedValueCents - a.agreedValueCents)
        .sort((a, b) => b.estimatedValueCents - a.estimatedValueCents)
        .sort((a, b) => b.originalValueCents - a.originalValueCents)
      return items.map(variation => ({
        ...variation,
        parts: variation.parts.map(variationPart => {
          const part = this.parts.find(i => variationPart.partId === i.id)
          return {
            ...part,
            ...variationPart
          }
        })
      }))
    }
  },
  watch: {
    showParts (v) {
      this.expandedItems = v ? cloneDeep(this.sortedItems) : []
    }
  }
}
</script>

<style scoped>
.notes-cell {
  max-width: 350px;
  white-space: pre-wrap;
  word-break: break-word;
}
.pdf-mode.orange {
  background-color: orange !important;
}
.pdf-mode.primary {
  background-color: #2071b5 !important;
}
.pdf-mode.success {
  background-color: #4caf50 !important;
}
.pdf-mode.error {
  background-color: #b71c1c !important;
}
.pdf-mode.yellow {
  background-color: #ffeb3b !important;
}
.pdf-mode.footer {
  width: 100%;
  text-align: end;
}
.pdf-mode.v-chip {
  margin: 0.9rem 0 !important;
}
.pdf-mode {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.container--fluid {
  padding-top: 0.25rem !important;
  padding-bottom: 0 !important;
}
</style>
